import React from 'react';
import { Box, Card, CardContent, Typography, Link, Chip } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FavoriteIcon from '@mui/icons-material/Favorite';
import ChatBubbleIcon from '@mui/icons-material/ChatBubble';
import ShareIcon from '@mui/icons-material/Share';

function TikTokEmbed({ videoUrl, description, video }) {
  const url = videoUrl || video?.url || video?.video_url;
  const desc = description || video?.description || video?.text || 'TikTok Video';
  const views = video?.views;
  const likes = video?.likes;
  const comments = video?.comments;
  const shares = video?.shares;

  const getVideoId = (url) => {
    if (!url) return null;

    // Try different URL patterns
    const patterns = [
      /video\/(\d+)/,                    // Standard format
      /\/v\/(\d+)/,                      // Short format
      /tiktok\.com\/@[\w.]+\/(\d+)/,     // Profile format
      /vm\.tiktok\.com\/(\w+)/           // VM format
    ];

    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }

    console.log('Could not extract video ID from URL:', url);
    return null;
  };

  const formatNumber = (num) => {
    if (!num) return '0';
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  const videoId = getVideoId(url);
  if (!videoId) return null;

  return (
    <Card sx={{
      width: '100%',
      borderRadius: 2,
      boxShadow: '0px 4px 15px rgba(0, 0, 0, 0.1)',
      '&:hover': {
        boxShadow: '0px 6px 20px rgba(0, 0, 0, 0.15)',
        transform: 'translateY(-2px)',
        transition: 'all 0.2s'
      }
    }}>
      <Box sx={{
        width: '100%',
        position: 'relative',
        paddingTop: '177.77%', // 16:9 aspect ratio
        '& iframe': {
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          border: 'none',
        }
      }}>
        <iframe
          title={`TikTok video ${videoId}`}
          src={`https://www.tiktok.com/embed/v2/${videoId}`}
          allowFullScreen
          allow="encrypted-media;"
        />
      </Box>
      <CardContent sx={{ p: 2 }}>
        <Link
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            textDecoration: 'none',
            '&:hover': { textDecoration: 'underline' }
          }}
        >
          <Typography
            variant="body2"
            sx={{
              mb: 1,
              fontWeight: 500,
              color: 'text.primary',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {desc}
          </Typography>
        </Link>

        {(views || likes || comments || shares) && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
            {views && (
              <Chip
                icon={<VisibilityIcon sx={{ fontSize: 14 }} />}
                label={formatNumber(views)}
                size="small"
                sx={{ fontSize: '0.7rem' }}
              />
            )}
            {likes && (
              <Chip
                icon={<FavoriteIcon sx={{ fontSize: 14 }} />}
                label={formatNumber(likes)}
                size="small"
                sx={{ fontSize: '0.7rem' }}
              />
            )}
            {comments && (
              <Chip
                icon={<ChatBubbleIcon sx={{ fontSize: 14 }} />}
                label={formatNumber(comments)}
                size="small"
                sx={{ fontSize: '0.7rem' }}
              />
            )}
            {shares && (
              <Chip
                icon={<ShareIcon sx={{ fontSize: 14 }} />}
                label={formatNumber(shares)}
                size="small"
                sx={{ fontSize: '0.7rem' }}
              />
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

export default TikTokEmbed;
